import {cn} from '../lib/utils'
import {Icon, type IconName} from './Icon'
import {cva, type VariantProps} from 'class-variance-authority'
import {forwardRef, type ReactNode} from 'react'

/**
 * Figma: https://www.figma.com/design/qS60CfMVFGs07l2G3yfhsB/%F0%9F%92%A0-Components?node-id=16249-8431&m=dev
 */

const actionCard = cva(
  'group flex w-full items-center rounded-lg border border-border bg-surface transition-colors',
  {
    variants: {
      variant: {
        default: 'p-[19px]',
        checkbox: 'p-[19px]',
        avatar: 'px-[15px] py-[13px]',
        image: 'px-[15px] py-[19px]',
        products: 'p-[15px]',
      },
      state: {
        default: 'hover:border-border-hover',
        pressed: 'border-border-pressed bg-surface-hover',
        selected: 'border-border-selected',
        disabled: 'cursor-default border-border-disabled bg-surface-secondary',
        focused: 'border-border-focus bg-background',
      },
    },
    defaultVariants: {
      variant: 'default',
      state: 'default',
    },
  },
)

export interface ActionCardProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'title'>,
    VariantProps<typeof actionCard> {
  /** Title text - displayed in medium weight */
  title: string
  /** Description text - displayed below title in light weight */
  description?: string
  /** Leading icon name from the icon library */
  leadingIcon?: IconName
  /** Leading avatar element (alternative to icon) */
  leadingAvatar?: ReactNode
  /** Leading image element (alternative to icon) */
  leadingImage?: ReactNode
  /** Leading products images (alternative to icon) */
  leadingProducts?: ReactNode
  /** Show customize icon over the products image */
  hasCustomMadeIcon?: boolean
  /** Show trailing chevron icon */
  showTrailingIcon?: boolean
  /** Show trailing checkbox icon */
  showTrailingCheckbox?: boolean
  /** Extra content rendered inside the text container, above the title (e.g. badges, corner icons) */
  children?: ReactNode
  /** Click handler */
  onClick?: () => void
  /** Disabled state */
  disabled?: boolean
}

export const ActionCard = forwardRef<HTMLButtonElement, ActionCardProps>(
  (
    {
      className,
      variant,
      state,
      title,
      description,
      leadingIcon,
      leadingAvatar,
      leadingImage,
      leadingProducts,
      hasCustomMadeIcon,
      showTrailingIcon = true,
      showTrailingCheckbox,
      children,
      onClick,
      disabled,
      ...props
    },
    ref,
  ) => {
    const cardVariant = leadingAvatar
      ? 'avatar'
      : leadingImage
        ? 'image'
        : leadingProducts
          ? 'products'
          : variant

    const visualState = disabled ? 'disabled' : (state ?? 'default')

    const leadingGapClass =
      leadingAvatar || leadingProducts
        ? 'gap-20'
        : leadingIcon || leadingImage
          ? 'gap-12'
          : undefined

    const iconColor =
      visualState === 'disabled'
        ? 'disabled'
        : visualState === 'selected'
          ? 'selected'
          : visualState === 'pressed'
            ? 'pressed'
            : 'default'

    return (
      <button
        ref={ref}
        type="button"
        className={cn(actionCard({variant: cardVariant, state: visualState}), className)}
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
        {...props}
      >
        <div className={cn('flex flex-1 items-center', leadingGapClass)}>
          {leadingIcon && (
            <div className="flex h-20 w-20 flex-shrink-0 items-center justify-center">
              <Icon
                name={leadingIcon}
                size={20}
                color={iconColor}
                className={cn(visualState === 'disabled' && 'text-text-disabled')}
              />
            </div>
          )}

          {/* Leading Avatar */}
          {leadingAvatar && (
            <div
              className={cn(
                'size-60 flex-shrink-0 overflow-hidden rounded-full bg-background-gray-dark transition-opacity',
                visualState === 'disabled' && 'opacity-50',
                visualState !== 'disabled' && 'group-hover:opacity-60',
              )}
            >
              {leadingAvatar}
            </div>
          )}

          {/* Leading Image */}
          {leadingImage && (
            <div
              className={cn(
                'h-[34px] w-[34px] flex-shrink-0',
                visualState === 'disabled' && 'opacity-50',
              )}
            >
              {leadingImage}
            </div>
          )}

          {/* Leading Products */}
          {leadingProducts && (
            <div
              className={cn(
                'md:w-[130px] md:h-[156px] relative flex h-[108px] w-[90px] flex-shrink-0 flex-col items-center justify-center',
                visualState === 'disabled' && 'opacity-50',
              )}
            >
              <div className="inset-0 absolute -rotate-[5deg] overflow-hidden rounded-md border border-border bg-background-gray-dark" />

              <div className="relative flex h-full w-full flex-col items-center justify-center overflow-hidden rounded-md border border-border bg-background-gray-dark">
                {leadingProducts}
              </div>

              {hasCustomMadeIcon && (
                <div className="backdrop-blur-sm absolute top-[6px] left-[6px] flex h-[22px] w-[22px] items-center justify-center rounded-md bg-white-alpha-600">
                  <Icon name={'custom-made'} size={14} />
                </div>
              )}
            </div>
          )}

          {/* Text Container */}
          <div
            className={cn(
              'flex flex-1 flex-col items-start gap-2 pr-20 transition-colors',
              visualState === 'default' && 'text-text',
              visualState === 'disabled' && 'text-text-disabled',
              visualState === 'selected' && 'text-text-selected',
              visualState === 'pressed' && 'text-text-pressed',
              visualState !== 'disabled' && 'group-hover:text-text-hover',
            )}
          >
            {children}

            {/* Title */}
            <div className="text-left">
              <span className="display-block text-base leading-base font-medium">{title}</span>
            </div>

            {/* Description */}
            {description && (
              <span className="display-block w-full text-left text-sm leading-sm font-light">
                {description}
              </span>
            )}
          </div>
        </div>

        {showTrailingIcon && (
          <div className="flex h-24 w-24 flex-shrink-0 items-center justify-center">
            <Icon
              name="chevron-right"
              size={24}
              color={iconColor}
              className={cn(
                'transition-colors',
                visualState === 'disabled' && 'text-text-disabled',
                visualState !== 'disabled' && 'group-hover:text-icon-hover',
              )}
            />
          </div>
        )}

        {showTrailingCheckbox && (
          <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center">
            <span
              className={cn(
                'block size-16 rounded-md border border-border-button-secondary transition-colors',
                visualState === 'default' && 'text-text',
                visualState === 'disabled' && 'border-border-button-secondary-disabled',
                visualState === 'selected' && 'border-button-selected bg-button-selected',
                visualState === 'pressed' && 'border-border-button-secondary-pressed',
              )}
            >
              <span
                className={cn(
                  'flex size-full shrink-0 items-center justify-center text-icon-inverse transition-opacity',
                  visualState === 'selected' && 'opacity-100',
                  visualState !== 'selected' && 'opacity-0',
                )}
                aria-hidden="true"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 1000 1000"
                  width="1em"
                  height="1em"
                  className="h-full w-auto"
                >
                  <title>Checkbox</title>
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M695.275 300.394a.5.5 0 0 0-.732.001l-27.181 29.393c-75.834 81.439-156.724 173.864-232.487 256.458a.5.5 0 0 1-.698.036l-104.465-92.159-29.828-26.3a.5.5 0 0 0-.727.071l-48.874 63.741a.5.5 0 0 0 .065.678l29.632 26.336L412.6 675.704l27.187 23.973a.5.5 0 0 0 .698-.036l24.531-26.553c84.01-90.227 175.027-195.745 257.438-284.258l27.231-29.447a.5.5 0 0 0-.002-.681z"
                  />
                </svg>
              </span>
            </span>
          </div>
        )}
      </button>
    )
  },
)

ActionCard.displayName = 'ActionCard'
